/**
 * Aura icon + curation-category loader (#137/#139) — resolves an aura def id to
 * a bundled image URL and its nameplate CURATION category, entirely client-side
 * (presentation layer). SHARED by the self buff/debuff bar (#139) and the
 * nameplate/frame aura rows (#137/#138) — one icon pipeline, different feeds.
 *
 * BOUNDARY: icons + curation are cosmetic/presentation and MUST NOT touch the
 * sim or any hash.
 *
 * BUFF/DEBUFF POLARITY (#168) IS NO LONGER HERE. It is server-authored on the
 * aura def (`AuraDef.polarity`) and rides the wire (`WireAura.polarity`), so the
 * client reads it directly off the snapshot — there is NO local buff/debuff
 * mirror table and NO "unknown → buff" silent default any more. This file keeps
 * ONLY the CURATION `category` axis (#137, a nameplate-crowding filter), which is
 * a separate concern still resolved client-side by def id.
 *
 * ASSET RESOLUTION: PNGs live in `packages/content/assets/icons/auras/
 * <aura-id>.png` (see that folder's README + #72 spec: 256x256, transparent,
 * art-only). Vite's `import.meta.glob` maps each file to a content-hashed URL
 * at build time. `auraIconSrc(defId)` returns that URL, or `null` when no art
 * is committed — callers fall back to a letter glyph (`auraIconGlyph`), exactly
 * like the ActionBar ability-icon fallback (#72). Ships safely before any art.
 */

/**
 * Aura "category" axis (#137) — the CURATION dimension the nameplate /
 * unit-frame MAJOR-aura rows filter on. Distinct from buff/debuff polarity
 * (#168, now wire-carried on `WireAura.polarity`, which drives border colour):
 * category answers "is this worth a slot on a crowded nameplate?".
 *
 *   • "cc"        — hard/soft crowd control (stun, root, silence). Always major.
 *   • "defensive" — big cooldown mitigation / immunity (shield, spell-reflect).
 *   • "debuff"    — a key applied debuff worth tracking on an enemy.
 *   • "minor"     — everything else (incidental tick-DoTs, permanent passives,
 *                   uncategorised auras). Hidden from the major rows by default.
 *
 * The DEFAULT selection ({@link DEFAULT_NAMEPLATE_AURA_CATEGORIES}) shows
 * cc/defensive/debuff and hides minor; the exact set is user-customizable via
 * prefs (`hud.nameplateAuraCategories`, see prefs.ts) — never hardcoded at the
 * call site.
 */
export const AURA_CATEGORIES = ["cc", "defensive", "debuff", "minor"] as const;
export type AuraCategory = (typeof AURA_CATEGORIES)[number];

/**
 * Default MAJOR-aura selection for nameplates / unit frames (#137): the curated
 * "worth a slot" categories. `minor` is intentionally excluded so tick-DoTs and
 * permanent passives don't clutter a crowded nameplate. Prefs override this.
 */
export const DEFAULT_NAMEPLATE_AURA_CATEGORIES: readonly AuraCategory[] = [
  "cc",
  "defensive",
  "debuff",
];

/**
 * Display priority within a major-aura row: lower sorts first (leftmost). CC is
 * the most decision-relevant (peel / trinket), then big defensives, then
 * debuffs, then the (normally hidden) minor tail. A TOTAL order over the
 * category axis so row order never wobbles between equal auras.
 */
const CATEGORY_PRIORITY: Readonly<Record<AuraCategory, number>> = {
  cc: 0,
  defensive: 1,
  debuff: 2,
  minor: 3,
};

/** Sort weight for an aura category (lower = leftmost in a major-aura row). */
export function auraCategoryPriority(category: AuraCategory): number {
  return CATEGORY_PRIORITY[category];
}

/**
 * Client-side per-aura CURATION metadata, keyed by def id. Holds ONLY the #137
 * `category` (nameplate-crowding filter) — the buff/debuff polarity that used to
 * live here is now server-authored and wire-carried (`WireAura.polarity`, #168).
 * Add a row when a new aura def lands; an unknown def falls back to
 * {@link DEFAULT_AURA_META} (categorised `minor`, so it stays off nameplates by
 * default until a row is added).
 *
 * RESERVED (not built at v1): a `hidden`/`hideFromSelfBar` flag to exclude
 * no-op/internal carrier auras from the self bar. v1 shows literally EVERY own
 * aura (all current defs are player-meaningful — a DoT, CC, or defensive buff —
 * none are internal carriers). Add the flag + a `selfBarAuras` filter here when
 * a genuinely-internal aura lands, mirroring the reserved dispel-type axis.
 */
interface AuraMeta {
  /** Curation category (#137) — drives the nameplate/frame major-aura filter. */
  readonly category: AuraCategory;
}

/**
 * Per-def curation metadata. Mirror the aura defs in
 * `packages/content/data/auras/<id>.json`; the category reflects how prominent
 * the aura is on a crowded nameplate (a CC/defensive/key-debuff is major; an
 * incidental tick or passive is minor). Buff/debuff polarity is NOT here (#168).
 */
const AURA_META_BY_ID: Readonly<Record<string, AuraMeta>> = {
  // #163: keyed by opaque aura-def id (trailing comment = human slug).
  // #137: `category` curates the nameplate/frame major-aura rows.
  aur_Z20ELeJdWe: { category: "debuff" }, // slug: corruption — key magic debuff on the target
  aur_J8eoAOc8sD: { category: "cc" }, // slug: stun — hard CC
  aur_frhKCv4vu5: { category: "defensive" }, // slug: spellreflect — defensive self-buff
  aur_lk2JVljN35: { category: "minor" }, // slug: thorns — permanent passive reflect (not nameplate-major)
};

/** Fallback for an unmapped def: categorised minor (an uncategorised aura is
 *  not curated-major, so it stays off nameplates by default until a row is
 *  added above). */
const DEFAULT_AURA_META: AuraMeta = { category: "minor" };


/**
 * Build-time map of every aura icon PNG -> its bundled URL. Eager + `?url`
 * so each entry is a ready string. Glob base is the content assets folder
 * (outside the client package; Vite `server.fs.allow` includes the repo root).
 * Indexed by the trailing `<aura-id>` key (the def id).
 *
 * In non-Vite environments (vitest), `import.meta.glob` is undefined -> empty
 * map -> every lookup returns null -> letter-glyph fallback. Tests exercise the
 * key/fallback logic without real assets.
 */
function loadIconUrlMap(): ReadonlyMap<string, string> {
  // `import.meta.glob(...)` MUST stay a literal call — Vite only transforms the
  // literal form; a cast/aliased `import.meta.glob` is left undefined at build
  // so assets never bundle (see #181).
  const modules = import.meta.glob<string>(
    "../../../../packages/content/assets/icons/auras/**/*.png",
    { eager: true, query: "?url", import: "default" },
  );
  const map = new Map<string, string>();
  for (const [path, url] of Object.entries(modules)) {
    const key = iconKeyFromPath(path);
    if (key !== null) map.set(key, url);
  }
  return map;
}

/**
 * Extract the `<aura-id>` key (no extension) from a glob module path.
 * e.g. ".../assets/icons/auras/corruption.png" -> "corruption".
 * Returns null if the path doesn't sit under an `auras/` segment.
 */
function iconKeyFromPath(path: string): string | null {
  const marker = "/auras/";
  const idx = path.lastIndexOf(marker);
  if (idx === -1) return null;
  const tail = path.slice(idx + marker.length);
  return tail.replace(/\.png$/u, "");
}

const ICON_URL_BY_KEY: ReadonlyMap<string, string> = loadIconUrlMap();

/**
 * Resolve the bundled image URL for an aura def id, or `null` when no art is
 * committed/loadable. Pure lookup; callers fall back to {@link auraIconGlyph}.
 */
export function auraIconSrc(defId: string): string | null {
  return ICON_URL_BY_KEY.get(defId) ?? null;
}

/**
 * The single-character glyph fallback for an aura with no bundled icon: first
 * letter of the def id, uppercased. Mirrors the ActionBar letter-glyph fallback
 * (#72) so an unmapped aura always renders something legible.
 */
export function auraIconGlyph(defId: string): string {
  return defId.charAt(0).toUpperCase();
}

/** Curation category for an aura def (#137; unknown → minor). */
export function auraCategory(defId: string): AuraCategory {
  return (AURA_META_BY_ID[defId] ?? DEFAULT_AURA_META).category;
}
